import { Component, OnDestroy, OnInit, computed, effect } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { faShieldHalved } from '@fortawesome/pro-solid-svg-icons';

import { MfaService } from '../../services/mfa.service';
import { MfaEnrollmentModalComponent } from '../mfa-enrollment-modal/mfa-enrollment-modal.component';

@Component({
  selector: 'app-mfa-grace-banner',
  standalone: false,
  templateUrl: './mfa-grace-banner.component.html',
  styleUrls: ['./mfa-grace-banner.component.scss'],
})
export class MfaGraceBannerComponent implements OnInit, OnDestroy {
  faShieldHalved = faShieldHalved;

  // Only prompt while inside the grace window. Once expired the enforcement
  // guard takes over with a full-page block, so the banner steps aside.
  readonly visible = computed(() => this.mfaService.shouldPrompt());
  readonly daysRemaining = computed(() => this.mfaService.status()?.daysRemaining ?? 0);

  // The dismissible pop-up shows once per local day; after that the banner
  // remains as a persistent reminder until the user enrols or the grace period
  // ends.
  private promptedDatesByUser = new Map<string, string>();
  private nextDailyCheckHandle?: ReturnType<typeof setTimeout>;
  private modalRef?: NgbModalRef;

  constructor(
    private auth: Auth,
    private mfaService: MfaService,
    private modal: NgbModal,
  ) {
    effect(() => {
      if (this.mfaService.shouldPrompt()) {
        this.maybeOpenDailyReminder();
      } else {
        this.clearDailyCheck();
      }
    });
  }

  ngOnInit(): void {
    void this.mfaService.refreshStatus();
  }

  ngOnDestroy(): void {
    this.clearDailyCheck();
  }

  readonly graceUntil = computed(() => this.mfaService.status()?.graceUntil ?? null);

  // "in 5 days" reads as an instruction to wait five days, so the deadline is
  // always phrased as a limit and paired with the date in the template.
  get deadlineLabel(): string {
    const days = this.daysRemaining();
    if (days <= 0) {
      return 'today';
    }
    return days === 1 ? 'within 1 day' : `within ${days} days`;
  }

  openSetup(options: { automatic?: boolean; userId?: string; dateKey?: string } = {}): void {
    if (this.modalRef) {
      this.scheduleNextDailyCheck();
      return;
    }

    const status = this.mfaService.status();
    const ref = this.modal.open(MfaEnrollmentModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'md',
    });
    ref.componentInstance.daysRemaining = status?.daysRemaining ?? 0;
    ref.componentInstance.graceUntil = status?.graceUntil ?? null;
    ref.componentInstance.dismissLabel = 'Remind me later';
    ref.componentInstance.showCloseButton = false;
    this.modalRef = ref;

    if (options.automatic && options.userId && options.dateKey) {
      this.persistPromptedDate(options.userId, options.dateKey);
    }

    void ref.result.catch(() => undefined).finally(() => {
      if (this.modalRef === ref) {
        this.modalRef = undefined;
      }
      // Refresh so the banner clears immediately if they enrolled in the modal.
      void this.mfaService.refreshStatus(true).finally(() => this.maybeOpenDailyReminder());
    });
  }

  private maybeOpenDailyReminder(): void {
    const userId = this.auth.currentUser?.uid;
    if (!userId) {
      return;
    }

    const dateKey = this.currentLocalDateKey();
    if (!this.hasPromptedOnDate(userId, dateKey)) {
      this.openSetup({ automatic: true, userId, dateKey });
      return;
    }

    this.scheduleNextDailyCheck();
  }

  private currentLocalDateKey(date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private storageKey(userId: string): string {
    return `mfa-grace-prompt-last-shown:${userId}`;
  }

  private hasPromptedOnDate(userId: string, dateKey: string): boolean {
    if (this.promptedDatesByUser.get(userId) === dateKey) {
      return true;
    }

    try {
      return localStorage.getItem(this.storageKey(userId)) === dateKey;
    } catch {
      return false;
    }
  }

  private persistPromptedDate(userId: string, dateKey: string): void {
    this.promptedDatesByUser.set(userId, dateKey);
    try {
      localStorage.setItem(this.storageKey(userId), dateKey);
    } catch {
      // The in-memory map still prevents duplicate prompts during this visit.
    }
  }

  private scheduleNextDailyCheck(): void {
    this.clearDailyCheck();

    if (!this.mfaService.shouldPrompt()) {
      return;
    }

    const now = new Date();
    const nextDay = new Date(now);
    nextDay.setHours(24, 0, 1, 0);
    const delay = Math.max(1000, nextDay.getTime() - now.getTime());

    this.nextDailyCheckHandle = setTimeout(() => {
      this.nextDailyCheckHandle = undefined;
      this.maybeOpenDailyReminder();
    }, delay);
  }

  private clearDailyCheck(): void {
    if (this.nextDailyCheckHandle) {
      clearTimeout(this.nextDailyCheckHandle);
      this.nextDailyCheckHandle = undefined;
    }
  }
}
